/**
 * Функции для работы с чатами и сообщениями
 */

import { supabase } from './supabase'

/**
 * Загрузить список чатов для пользователя
 * @param {string} userId - ID текущего пользователя
 * @returns {Promise<Array>} Массив чатов с данными собеседника и последним сообщением
 */
export async function loadChats(userId) {
  try {
    console.log('[loadChats] Loading chats for user:', userId)
    
    // Загружаем чаты где пользователь является участником
    const { data: chats, error: chatsError } = await supabase
      .from('chats')
      .select('id, match_id, user1_id, user2_id, created_at')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .order('created_at', { ascending: false })

    if (chatsError) {
      console.error('[loadChats] Error loading chats:', chatsError)
      return []
    }

    console.log('[loadChats] Raw chats loaded:', chats?.length || 0)

    if (!chats || chats.length === 0) {
      return []
    }

    // Для каждого чата загружаем данные собеседника, последнее сообщение и непрочитанные
    const chatsWithMessages = await Promise.all(
      chats.map(async (chat) => {
        const otherUserId = chat.user1_id === userId ? chat.user2_id : chat.user1_id
        
        // Загружаем профиль собеседника
        const { data: otherUserProfile } = await supabase
          .from('profiles')
          .select('id, name, photos, gender')
          .eq('id', otherUserId)
          .single()

        // Загружаем последнее сообщение (content, photo_url или gift для превью)
        const { data: lastMessage } = await supabase
          .from('messages')
          .select('id, content, photo_url, gift_type, sender_id, created_at, is_read')
          .eq('chat_id', chat.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        // Подсчитываем непрочитанные сообщения
        const { count: unreadCount } = await supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('chat_id', chat.id)
          .eq('is_read', false)
          .neq('sender_id', userId)

        // Обрабатываем фото: если это путь в Storage, получаем публичный URL
        let otherUserPhoto = null
        if (otherUserProfile?.photos?.[0]) {
          const photo = otherUserProfile.photos[0]
          if (photo.startsWith('http://') || photo.startsWith('https://')) {
            otherUserPhoto = photo
          } else {
            const { data } = supabase.storage
              .from('photos')
              .getPublicUrl(photo)
            otherUserPhoto = data.publicUrl
          }
        }

        const hasMessage = lastMessage?.content != null || lastMessage?.photo_url != null || lastMessage?.gift_type != null
        const preview = hasMessage
          ? (lastMessage?.content || (lastMessage?.photo_url ? 'Photo' : null) || (lastMessage?.gift_type ? `🎁 ${lastMessage.gift_type}` : null))
          : (chat.match_id ? 'No messages yet' : null)
        return {
          id: chat.id,
          matchId: chat.match_id,
          otherUserId,
          otherUserName: otherUserProfile?.name || 'Unknown',
          otherUserPhoto,
          otherUserGender: otherUserProfile?.gender || null,
          lastMessage: preview,
          lastMessageDate: lastMessage?.created_at || chat.created_at,
          unreadCount: unreadCount || 0,
          createdAt: chat.created_at
        }
      })
    )

    // Показываем чаты с сообщениями или матч-чаты (пустые после "Maybe later")
    const withMessages = chatsWithMessages.filter((c) => c.lastMessage != null)

    // Сортируем по дате последнего сообщения
    withMessages.sort((a, b) => {
      const dateA = new Date(a.lastMessageDate)
      const dateB = new Date(b.lastMessageDate)
      return dateB - dateA
    })

    console.log('[loadChats] ✅ Chats with messages loaded:', withMessages.length)
    return withMessages
  } catch (error) {
    console.error('[loadChats] Exception:', error)
    return []
  }
}

/**
 * Загрузить сообщения чата
 * @param {string} chatId - ID чата
 * @param {string} userId - ID текущего пользователя
 * @returns {Promise<Array>} Массив сообщений
 */
export async function loadMessages(chatId, userId) {
  try {
    console.log('[loadMessages] Loading messages for chat:', chatId)
    
    const { data: messages, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: true })

    if (messagesError) {
      console.error('[loadMessages] Error loading messages:', messagesError)
      return []
    }

    // Помечаем сообщения как прочитанные (только те, которые отправил не текущий пользователь)
    const { error: updateError } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('chat_id', chatId)
      .eq('is_read', false)
      .neq('sender_id', userId)

    if (updateError) {
      console.error('[loadMessages] Error marking messages as read:', updateError)
    } else {
      console.log('[loadMessages] ✅ Messages marked as read')
    }

    console.log('[loadMessages] ✅ Messages loaded:', messages?.length || 0)
    return messages || []
  } catch (error) {
    console.error('[loadMessages] Exception:', error)
    return []
  }
}

/**
 * Пометить одно сообщение как прочитанное (когда пользователь видит его в реальном времени)
 * @param {string} messageId - ID сообщения
 * @returns {Promise<void>}
 */
export async function markMessageAsRead(messageId) {
  try {
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('id', messageId)
      .eq('is_read', false)
  } catch (error) {
    console.error('[markMessageAsRead] Error:', error)
  }
}

/**
 * Отправить сообщение (текст и/или фото)
 * @param {string} chatId - ID чата
 * @param {string} senderId - ID отправителя
 * @param {string} content - Текст сообщения (может быть пустым для фото)
 * @param {string} [photoUrl] - URL фото (опционально)
 * @param {string} [giftType] - тип подарка (опционально)
 * @param {number} [giftPrice] - цена подарка (опционально)
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function sendMessage(chatId, senderId, content, photoUrl = null, giftType = null, giftPrice = null) {
  try {
    const hasContent = content != null && String(content).trim().length > 0
    const hasPhoto = photoUrl != null && String(photoUrl).trim().length > 0
    const hasGift = giftType != null && String(giftType).trim().length > 0
    if (!hasContent && !hasPhoto && !hasGift) {
      return { data: null, error: 'Message must have text, photo, or gift' }
    }

    // Валидация размера сообщения (максимум 1000 символов)
    if (hasContent && String(content).trim().length > 1000) {
      return { data: null, error: 'Message is too long. Maximum 1000 characters.' }
    }

    console.log('[sendMessage] Sending message to chat:', chatId, hasPhoto ? '(with photo)' : hasGift ? '(gift)' : '')
    
    // Получаем ID получателя сообщения (другого участника чата)
    const { data: chat } = await supabase
      .from('chats')
      .select('user1_id, user2_id')
      .eq('id', chatId)
      .single()

    const recipientId = chat?.user1_id === senderId ? chat?.user2_id : chat?.user1_id
    
    const payload = {
      chat_id: chatId,
      sender_id: senderId,
      content: hasContent ? String(content).trim() : null,
      is_read: false
    }
    if (hasPhoto) payload.photo_url = String(photoUrl).trim()
    if (hasGift) {
      payload.gift_type = String(giftType).trim()
      payload.gift_price = giftPrice != null ? Number(giftPrice) : null
    }
    
    const { data, error } = await supabase
      .from('messages')
      .insert(payload)
      .select()
      .single()

    if (error) {
      console.error('[sendMessage] Error sending message:', error)
      return { data: null, error: error.message }
    }

    // Отправляем email уведомление получателю (только для текстовых сообщений, не для подарков)
    if (recipientId && !hasGift) {
      const { sendEmailNotification } = await import('./sendEmailNotification')
      sendEmailNotification('new_message', recipientId, { chatId, fromUserId: senderId }).catch(err => 
        console.error('[sendMessage] Email notification error:', err)
      )
    }

    console.log('[sendMessage] ✅ Message sent:', data.id)
    return { data, error: null }
  } catch (error) {
    console.error('[sendMessage] Exception:', error)
    return { data: null, error: error.message }
  }
}

/**
 * Найти существующий чат с пользователем (без создания)
 * @param {string} userId - ID текущего пользователя
 * @param {string} otherUserId - ID другого пользователя
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function getExistingChat(userId, otherUserId) {
  try {
    const { data, error } = await supabase
      .from('chats')
      .select('id, match_id, user1_id, user2_id')
      .or(`and(user1_id.eq.${userId},user2_id.eq.${otherUserId}),and(user1_id.eq.${otherUserId},user2_id.eq.${userId})`)
      .limit(1)
      .maybeSingle()
    if (error) return { data: null, error: error.message }
    return { data, error: null }
  } catch (e) {
    console.error('[getExistingChat]', e)
    return { data: null, error: e?.message }
  }
}

/**
 * Получить или создать чат с пользователем
 * @param {string} userId - ID текущего пользователя
 * @param {string} otherUserId - ID другого пользователя
 * @param {string|null} matchId - ID матча (если есть)
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function getOrCreateChat(userId, otherUserId, matchId = null) {
  try {
    console.log('[getOrCreateChat] Getting or creating chat:', { userId, otherUserId, matchId })
    
    // Проверяем существующий чат
    const { data: existingChat } = await supabase
      .from('chats')
      .select('id')
      .or(`and(user1_id.eq.${userId},user2_id.eq.${otherUserId}),and(user1_id.eq.${otherUserId},user2_id.eq.${userId})`)
      .limit(1)
      .maybeSingle()

    if (existingChat) {
      console.log('[getOrCreateChat] Existing chat found:', existingChat.id)
      return { data: existingChat, error: null }
    }

    // Создаем новый чат
    const user1Id = userId < otherUserId ? userId : otherUserId
    const user2Id = userId < otherUserId ? otherUserId : userId

    const { data: newChat, error: createError } = await supabase
      .from('chats')
      .insert({
        match_id: matchId,
        user1_id: user1Id,
        user2_id: user2Id
      })
      .select()
      .single()

    if (createError) {
      console.error('[getOrCreateChat] Error creating chat:', createError)
      return { data: null, error: createError.message }
    }

    console.log('[getOrCreateChat] ✅ New chat created:', newChat.id)
    return { data: newChat, error: null }
  } catch (error) {
    console.error('[getOrCreateChat] Exception:', error)
    return { data: null, error: error.message }
  }
}
